import React, { useEffect, useState, useCallback, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  SupportAgent,
  FiberManualRecord,
  Search,
  Refresh,
  PersonOff,
} from '@mui/icons-material';
import Title from '../../components/Title';
import TableCell from '../../components/TableCell';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import {
  subscribeToTickets,
  getTicketCounts,
  formatDuration,
} from '../../services/ticketService';
import {
  Ticket,
  TicketFilters,
  TicketStatus,
  TicketCategory,
  TicketPriority,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  STATUS_LABELS,
  STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
} from '../../resources/types/ticketTypes';
import TicketChatModal from '../../modals/TicketChatModal/TicketChatModal';

const Root = styled('div')({
  minHeight: 'calc(100vh - 180px)',
});

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  color: theme.palette.text.primary,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(78, 205, 196, 0.08)'
        : 'rgba(78, 205, 196, 0.12)',
  },
}));

const CounterCard = styled(Paper)(({ theme }) => ({
  padding: '16px 20px',
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: 120,
  flex: 1,
  border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
}));

const TicketsPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const auth = useSelector(selectAuth);
  const adminInfo = useSelector(selectAdmins);
  const adminData = adminInfo.info as any;
  const adminId = adminData?._id || adminData?.login || '';

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TicketFilters>({ status: 'all' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
  });
  const unsubRef = useRef<(() => void) | null>(null);

  const loadCounts = useCallback(async () => {
    try {
      const c = await getTicketCounts();
      setCounts(c);
    } catch (err) {
      console.error('Error loading counts:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);

    if (unsubRef.current) {
      unsubRef.current();
    }

    unsubRef.current = subscribeToTickets(filters, adminId, (data) => {
      setTickets(data);
      setLoading(false);
    });

    loadCounts();

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
      }
    };
  }, [filters, adminId, loadCounts]);

  const handleRefresh = useCallback(() => {
    loadCounts();
  }, [loadCounts]);

  const handleOpenTicket = useCallback((ticketId: string) => {
    setSelectedTicketId(ticketId);
    setChatModalOpen(true);
  }, []);

  const handleCloseChat = useCallback(() => {
    setChatModalOpen(false);
    setSelectedTicketId(null);
    loadCounts();
  }, [loadCounts]);

  const handleStatusFilter = (_: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
    if (newStatus !== null) {
      setFilters((prev) => ({ ...prev, status: newStatus as TicketStatus | 'all' }));
    }
  };

  const filteredTickets = searchQuery
    ? tickets.filter(
        (ticket) =>
          ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ticket.metadata?.scooterId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ticket.userId?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tickets;

  return (
    <Root>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportAgent sx={{ color: '#4ECDC4', fontSize: 32 }} />
          <Title>{t('tickets.title')}</Title>
        </Box>
        <Tooltip title={t('tickets.refresh')}>
          <IconButton onClick={handleRefresh}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <CounterCard>
          <Typography variant="h4" sx={{ fontWeight: 700, color: STATUS_COLORS.open }}>
            {counts.open || 0}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('tickets.statusOpen')}
          </Typography>
        </CounterCard>
        <CounterCard>
          <Typography variant="h4" sx={{ fontWeight: 700, color: STATUS_COLORS.in_progress }}>
            {counts.in_progress || 0}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('tickets.statusInProgress')}
          </Typography>
        </CounterCard>
        <CounterCard>
          <Typography variant="h4" sx={{ fontWeight: 700, color: STATUS_COLORS.resolved }}>
            {counts.resolved || 0}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('tickets.statusResolved')}
          </Typography>
        </CounterCard>
        <CounterCard>
          <Typography variant="h4" sx={{ fontWeight: 700, color: STATUS_COLORS.closed }}>
            {counts.closed || 0}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('tickets.statusClosed')}
          </Typography>
        </CounterCard>
      </Box>

      <Paper
        sx={{
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <ToggleButtonGroup
            value={filters.status || 'all'}
            exclusive
            onChange={handleStatusFilter}
            size="small"
          >
            <ToggleButton value="all">{t('tickets.statusAll')}</ToggleButton>
            <ToggleButton value="open">
              <FiberManualRecord sx={{ fontSize: 10, mr: 0.5, color: STATUS_COLORS.open }} />
              {t('tickets.statusOpen')}
            </ToggleButton>
            <ToggleButton value="in_progress">
              <FiberManualRecord sx={{ fontSize: 10, mr: 0.5, color: STATUS_COLORS.in_progress }} />
              {t('tickets.statusInProgress')}
            </ToggleButton>
            <ToggleButton value="resolved">
              <FiberManualRecord sx={{ fontSize: 10, mr: 0.5, color: STATUS_COLORS.resolved }} />
              {t('tickets.statusResolved')}
            </ToggleButton>
            <ToggleButton value="closed">
              <FiberManualRecord sx={{ fontSize: 10, mr: 0.5, color: STATUS_COLORS.closed }} />
              {t('tickets.statusClosed')}
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('tickets.filterCategory')}</InputLabel>
            <Select
              value={filters.category || 'all'}
              label={t('tickets.filterCategory')}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  category: e.target.value as TicketCategory | 'all',
                }))
              }
            >
              <MenuItem value="all">{t('tickets.categoryAll')}</MenuItem>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {CATEGORY_ICONS[key as TicketCategory]} {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{t('tickets.filterPriority')}</InputLabel>
            <Select
              value={filters.priority || 'all'}
              label={t('tickets.filterPriority')}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  priority: e.target.value as TicketPriority | 'all',
                }))
              }
            >
              <MenuItem value="all">{t('tickets.statusAll')}</MenuItem>
              {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  <FiberManualRecord
                    sx={{ fontSize: 10, mr: 0.5, color: PRIORITY_COLORS[key as TicketPriority] }}
                  />
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButton
            value="me"
            selected={filters.assignedTo === 'me'}
            onChange={() =>
              setFilters((prev) => ({
                ...prev,
                assignedTo: prev.assignedTo === 'me' ? 'all' : 'me',
              }))
            }
            size="small"
          >
            <PersonOff sx={{ fontSize: 18, mr: 0.5 }} />
            {t('tickets.myTickets')}
          </ToggleButton>

          <Box sx={{ flex: 1 }} />

          <TextField
            size="small"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress sx={{ color: '#4ECDC4' }} />
            </Box>
          ) : filteredTickets.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300,
                gap: 2,
              }}
            >
              <SupportAgent sx={{ fontSize: 48, color: theme.palette.text.disabled }} />
              <Typography color="textSecondary">{t('tickets.emptyTitle')}</Typography>
            </Box>
          ) : (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableHeaderCell sx={{ width: 6 }} />
                  <TableHeaderCell>{t('tickets.colCategory')}</TableHeaderCell>
                  <TableHeaderCell>{t('tickets.colSubject')}</TableHeaderCell>
                  <TableHeaderCell>{t('tickets.colScooter')}</TableHeaderCell>
                  <TableHeaderCell align="center">{t('tickets.colStatus')}</TableHeaderCell>
                  <TableHeaderCell align="center">{t('tickets.colMessages')}</TableHeaderCell>
                  <TableHeaderCell>{t('tickets.lastMessage')}</TableHeaderCell>
                  <TableHeaderCell>{t('tickets.colTime')}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.map((ticket) => (
                  <StyledTableRow key={ticket.id} onClick={() => handleOpenTicket(ticket.id)}>
                    <TableCell
                      sx={{
                        width: 6,
                        p: 0,
                        backgroundColor: PRIORITY_COLORS[ticket.priority],
                      }}
                    />
                    <TableCell>
                      <Typography variant="body2">
                        {CATEGORY_ICONS[ticket.category]} {CATEGORY_LABELS[ticket.category]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {ticket.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {ticket.metadata?.scooterId || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={STATUS_LABELS[ticket.status]}
                        size="small"
                        sx={{
                          backgroundColor: `${STATUS_COLORS[ticket.status]}20`,
                          color: STATUS_COLORS[ticket.status],
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={ticket.messagesCount || 0}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 32 }}
                      />
                    </TableCell>
                    <TableCell>
                      {ticket.lastMessage ? (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {ticket.lastMessage.sender === 'admin' ? '🟢 ' : ''}
                          {ticket.lastMessage.text}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {t('tickets.ago', {
                          time: formatDuration(Date.now() - new Date(ticket.updatedAt).getTime()),
                        })}
                      </Typography>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      <TicketChatModal
        open={chatModalOpen}
        ticketId={selectedTicketId}
        onClose={handleCloseChat}
      />
    </Root>
  );
};

export default TicketsPage;
